/**
 * Unified Primary Contacts Manager
 * Handles CRUD operations for primary contacts across all moment types
 */

import { updateMoment } from '../../RsdForms/api';
import { resolveContactEmail } from '../../../services/contactEmailUtils';

export interface Contact {
  id?: string;
  uid?: string;
  name: string;
  /** Canonical address; API `mail` is merged into this when loading (see flattenPrimaryContacts). */
  email?: string;
  /** Present on some API/LDAP rows before flatten. */
  mail?: string;
  role: string;
  status?: string;
  department?: string;
  imageUrl?: string;
  type?: string;
}

/** PATCH row marking a primary contact for deletion. */
export interface PrimaryContactDeleteOp {
  id?: string;
  _delete: true;
}

/** PATCH row for create/update. API field is `mail`. */
export interface PrimaryContactUpsertOp {
  id?: string;
  uid?: string;
  mail: string;
  name: string;
  status: string;
}

/** Minimal phase slice used when applying optimistic primary-contact updates. */
interface ReleasePhaseOptimistic {
  id?: string;
  primaryContacts?: Record<string, unknown[]>;
}

export interface PrimaryContactsApiFormat {
  productMarketingManagers?: any[];
  productManagers?: any[];
  programManagers?: any[];
}

export interface PrimaryContactsManagerOptions {
  issueId: string;
  isPhased: boolean;
  phaseId?: string;
  phaseName?: string;
  onOptimisticUpdate: (updater: (prev: any) => any) => void;
  getCurrentContacts: (phaseId?: string) => Contact[];
  onDataRefresh?: (patchResponse?: unknown) => void; // Optional callback for soft refresh after API operations
}

export class PrimaryContactsManager {
  private options: PrimaryContactsManagerOptions;
  private inflightRequests = new Map<string, boolean>();

  constructor(options: PrimaryContactsManagerOptions) {
    this.options = options;
  }

  private getRoleApiKey(role: string): string {
    const roleMapping: Record<string, string> = {
      'PROD MRKT MANAGER': 'productMarketingManagers',
      'PRODUCT MANAGER': 'productManagers',
      'PROGRAM MANAGER': 'programManagers'
    };
    return roleMapping[role] || 'productMarketingManagers';
  }

  private convertContactsToApiFormat(
    contacts: Contact[]
  ): PrimaryContactsApiFormat {
    const apiFormat: PrimaryContactsApiFormat = {
      productMarketingManagers: [],
      productManagers: [],
      programManagers: []
    };

    contacts.forEach(contact => {
      const addr = resolveContactEmail(contact);
      const contactData = {
        id: contact.id,
        uid: contact.uid,
        mail: addr,
        name: contact.name,
        status: contact.status || 'y',
        _delete: false
      };

      switch (contact.role) {
        case 'PROD MRKT MANAGER':
          apiFormat.productMarketingManagers!.push(contactData);
          break;
        case 'PRODUCT MANAGER':
          apiFormat.productManagers!.push(contactData);
          break;
        case 'PROGRAM MANAGER':
          apiFormat.programManagers!.push(contactData);
          break;
      }
    });

    return apiFormat;
  }

  private computeRoleDiffWithCurrentContacts(
    role: string,
    currentContacts: Contact[],
    newContacts: Contact[]
  ): {
    toDelete: PrimaryContactDeleteOp[];
    toUpsert: PrimaryContactUpsertOp[];
  } {
    const byUid = (arr: Contact[]) =>
      new Map(arr.map(c => [c.uid || c.name, c]));

    const currentMap = byUid(currentContacts);
    const newMap = byUid(newContacts);

    const toDelete: PrimaryContactDeleteOp[] = [];
    const toUpsert: PrimaryContactUpsertOp[] = [];

    currentMap.forEach(contact => {
      if (!newMap.has(contact.uid || contact.name)) {
        toDelete.push({ id: contact.id, _delete: true });
      }
    });

    newMap.forEach(contact => {
      const addr = resolveContactEmail(contact);
      toUpsert.push({
        id: contact.id,
        uid: contact.uid,
        mail: addr,
        name: contact.name,
        status: contact.status || 'y'
      });
    });

    return { toDelete, toUpsert };
  }

  private createApiPayload(
    role: string,
    operations: {
      toDelete: PrimaryContactDeleteOp[];
      toUpsert: PrimaryContactUpsertOp[];
    }
  ): any {
    const roleKey = this.getRoleApiKey(role);
    const contacts: Array<PrimaryContactDeleteOp | PrimaryContactUpsertOp> = [
      ...operations.toDelete,
      ...operations.toUpsert
    ];

    if (this.options.isPhased) {
      return {
        releasePhases: [
          {
            id: this.options.phaseId,
            primaryContacts: {
              [roleKey]: contacts
            }
          }
        ]
      };
    }
    return {
      primaryContacts: {
        [roleKey]: contacts
      }
    };
  }

  private updateUIOptimistically(role: string, newContacts: Contact[]): void {
    this.options.onOptimisticUpdate(prev => {
      if (!prev) return prev;

      const next = { ...prev };
      const roleKey = this.getRoleApiKey(role);

      const formattedContacts = newContacts.map(contact => {
        const addr = resolveContactEmail(contact);
        return {
          uid: contact.uid,
          mail: addr,
          email: addr,
          name: contact.name,
          status: contact.status || 'y',
          id: contact.id
        };
      });

      if (this.options.isPhased) {
        if (next.releasePhases) {
          next.releasePhases = next.releasePhases.map(
            (phase: ReleasePhaseOptimistic) =>
              phase.id === this.options.phaseId
                ? {
                    ...phase,
                    primaryContacts: {
                      ...(phase.primaryContacts || {}),
                      [roleKey]: formattedContacts
                    }
                  }
                : phase
          );
        }
      } else {
        if (!next.primaryContacts) {
          next.primaryContacts = {
            productMarketingManagers: [],
            programManagers: [],
            productManagers: []
          };
        }
        next.primaryContacts[roleKey] = formattedContacts;
      }

      return next;
    });
  }

  async addContacts(newContacts: Contact | Contact[]): Promise<void> {
    const contactsArray = Array.isArray(newContacts)
      ? newContacts
      : [newContacts];
    if (contactsArray.length === 0) return;

    const role = contactsArray[0].role;
    const roleKey = this.getRoleApiKey(role);
    const inflightKey = `${this.options.phaseId || 'nonphased'}-add-${roleKey}`;

    if (this.inflightRequests.get(inflightKey)) return;
    this.inflightRequests.set(inflightKey, true);

    try {
      const currentContacts = this.options.getCurrentContacts(
        this.options.phaseId
      );
      const allContacts = [...currentContacts, ...contactsArray];

      this.updateUIOptimistically(
        role,
        allContacts.filter(c => c.role === role)
      );

      const apiFormat = this.convertContactsToApiFormat(allContacts);
      const payload = this.options.isPhased
        ? {
            releasePhases: [
              {
                id: this.options.phaseId,
                primaryContacts: apiFormat
              }
            ]
          }
        : { primaryContacts: apiFormat };
      const response = await updateMoment(this.options.issueId, payload);

      if (this.options.onDataRefresh) {
        this.options.onDataRefresh(response);
      }
    } finally {
      this.inflightRequests.set(inflightKey, false);
    }
  }

  async replaceRoleContacts(
    role: string,
    newContacts: Contact[]
  ): Promise<void> {
    const roleKey = this.getRoleApiKey(role);
    const inflightKey = `${
      this.options.phaseId || 'nonphased'
    }-replace-${roleKey}`;

    if (this.inflightRequests.get(inflightKey)) return;
    this.inflightRequests.set(inflightKey, true);

    try {
      const contactsWithRole = newContacts.map(contact => ({
        ...contact,
        role
      }));

      const currentContactsBeforeUpdate = this.options
        .getCurrentContacts(this.options.phaseId)
        .filter(c => c.role === role);

      this.updateUIOptimistically(role, contactsWithRole);

      const operations = this.computeRoleDiffWithCurrentContacts(
        role,
        currentContactsBeforeUpdate,
        contactsWithRole
      );
      const payload = this.createApiPayload(role, operations);

      const response = await updateMoment(this.options.issueId, payload);

      if (this.options.onDataRefresh) {
        this.options.onDataRefresh(response);
      }
    } finally {
      this.inflightRequests.set(inflightKey, false);
    }
  }

  async updateContact(updatedContact: Contact, index: number): Promise<void> {
    const role = updatedContact.role;
    const currentContacts = this.options.getCurrentContacts(
      this.options.phaseId
    );
    const updatedContacts = [...currentContacts];
    updatedContacts[index] = updatedContact;

    const roleContacts = updatedContacts.filter(c => c.role === role);
    await this.replaceRoleContacts(role, roleContacts);
  }
}

export function createPrimaryContactsManager(
  options: PrimaryContactsManagerOptions
): PrimaryContactsManager {
  return new PrimaryContactsManager(options);
}
